/*
 * B&B FIREARMS — storefront behaviour (V1)
 *
 * Runs alongside Dawn's global.js on store pages. It must not assume Dawn's
 * classes exist and must never touch the ELR funnel, which loads elr.js
 * instead and shares nothing with this file.
 *
 * Responsibilities:
 *   1. Mobile navigation drawer with collapsible groups
 *   2. Reveal-on-scroll for [data-bb-reveal]
 *   3. Mobile sticky buy bar on product pages
 *   4. The Build My ELR System qualification funnel (submits to the same
 *      Shopify contact endpoint via a hidden iframe as the ELR application)
 *   5. window.bbTrack() — one hook for the three conversions we care about:
 *      product purchase intent, complete-system lead, ELR experience lead
 *   6. Compact FAQ / events disclosures (aria-expanded + restrained motion)
 */
(function () {
  'use strict';

  /* ----------------------------------------------------------- tracking --- */
  /**
   * Report a storefront conversion event. Every collector is optional, so a
   * missing analytics tool can never break the page.
   * @param {string} name
   * @param {Object} [data]
   */
  function bbTrack(name, data) {
    if (!name) return;

    var payload = { event: name, store: 'bnbfirearms' };
    if (data) {
      Object.keys(data).forEach(function (k) {
        payload[k] = data[k];
      });
    }

    try {
      if (window.BBHeatmap && typeof window.BBHeatmap.track === 'function') {
        window.BBHeatmap.track(name, payload);
      }
    } catch (e) {
      /* tracking must never throw */
    }

    try {
      if (Array.isArray(window.dataLayer)) window.dataLayer.push(payload);
    } catch (e) {
      /* no-op */
    }

    try {
      if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
        window.Shopify.analytics.publish(name, payload);
      }
    } catch (e) {
      /* no-op */
    }
  }

  window.bbTrack = bbTrack;

  /* Declarative tracking for every value-ladder CTA. */
  function initTracking() {
    document.addEventListener(
      'click',
      function (event) {
        var t = event.target;
        if (!t || typeof t.closest !== 'function') return;

        var node = t.closest('[data-bb-track]');
        if (node) {
          bbTrack(node.getAttribute('data-bb-track'), {
            label:
              node.getAttribute('data-bb-track-label') ||
              (node.textContent || '').trim().slice(0, 80),
            path: window.location.pathname
          });
        }

        // Add-to-cart intent, wherever Dawn renders the button.
        var atc = t.closest('[name="add"], .product-form__submit');
        if (atc) {
          bbTrack('bb_add_to_cart_click', { path: window.location.pathname });
        }
      },
      { passive: true }
    );
  }

  /* -------------------------------------------------------------- drawer --- */
  function initDrawer() {
    var drawer = document.querySelector('[data-bb-drawer]');
    if (!drawer || drawer.hasAttribute('data-bb-bound')) return;
    drawer.setAttribute('data-bb-bound', '');

    var lastFocus = null;

    function open() {
      lastFocus = document.activeElement;
      drawer.setAttribute('data-open', 'true');
      document.body.style.overflow = 'hidden';
      var first = drawer.querySelector('button, a');
      if (first) first.focus();
    }

    function close() {
      drawer.setAttribute('data-open', 'false');
      document.body.style.overflow = '';
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    }

    document.addEventListener('click', function (event) {
      var t = event.target;
      if (!t || typeof t.closest !== 'function') return;

      if (t.closest('[data-bb-drawer-open]')) {
        event.preventDefault();
        open();
        return;
      }
      if (t.closest('[data-bb-drawer-close]') || t.closest('[data-bb-drawer-scrim]')) {
        event.preventDefault();
        close();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && drawer.getAttribute('data-open') === 'true') close();
    });

    // Collapsible groups inside the drawer.
    var toggles = drawer.querySelectorAll('[data-bb-drawer-toggle]');
    for (var i = 0; i < toggles.length; i++) {
      (function (btn) {
        btn.addEventListener('click', function () {
          var panel = document.getElementById(btn.getAttribute('aria-controls'));
          if (!panel) return;
          var isOpen = btn.getAttribute('aria-expanded') === 'true';
          btn.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
          panel.hidden = isOpen;
        });
      })(toggles[i]);
    }
  }

  /* -------------------------------------------------------------- reveal --- */
  function initReveal(root) {
    var targets = root.querySelectorAll('[data-bb-reveal]:not([data-bb-seen])');
    if (!targets.length) return;

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var i;

    if (reduce || typeof IntersectionObserver === 'undefined') {
      for (i = 0; i < targets.length; i++) {
        targets[i].setAttribute('data-bb-seen', '');
        targets[i].classList.add('is-in');
      }
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    for (i = 0; i < targets.length; i++) {
      targets[i].setAttribute('data-bb-seen', '');
      io.observe(targets[i]);
    }
  }

  /* --------------------------------------------------------- sticky buy --- */
  /**
   * On mobile the buy button scrolls away on a long product page. This mirrors
   * it in a fixed bar once the real form is out of view, and hides again when
   * the form returns so the two never compete.
   */
  function initStickyBuy() {
    var bar = document.querySelector('[data-bb-stickybuy]');
    if (!bar || bar.hasAttribute('data-bb-bound')) return;
    bar.setAttribute('data-bb-bound', '');

    var form = document.querySelector('product-form, .product-form, [data-bb-buyform]');
    if (!form || typeof IntersectionObserver === 'undefined') return;

    new IntersectionObserver(
      function (entries) {
        bar.hidden = entries[0].isIntersecting;
      },
      { threshold: 0 }
    ).observe(form);

    var cta = bar.querySelector('[data-bb-stickybuy-cta]');
    if (cta) {
      cta.addEventListener('click', function (event) {
        event.preventDefault();
        bbTrack('bb_stickybuy_click', { path: window.location.pathname });
        var target = document.querySelector('[name="add"], .product-form__submit');
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(function () {
            try {
              target.focus({ preventScroll: true });
            } catch (e) {
              /* focus is a nicety */
            }
          }, 400);
        }
      });
    }
  }

  /* ------------------------------------------------- system lead capture --- */
  /**
   * The Build My ELR System funnel. Uses the same proven pattern as the ELR
   * application: native Shopify contact form posted into a hidden iframe so
   * the visitor is never navigated away, with an inline success state.
   */
  /* ----------------------------------------------- build-sheet recs --- */
  /**
   * After a build sheet is sent, show up to four rifles that fit the answers.
   * Candidates are inert <template data-bb-rec> cards (snippets/bb-cfg-recs).
   *
   *  distance  -> a chambering must reach the far end of the band without
   *               gross overkill (hard filter, never relaxed; REACH table)
   *  investment-> the rifle's share of the system budget (cap ~70%)
   *  mission   -> type / model keywords (score)
   *  brand     -> Victrix first, then Cadex (score; highest-margin lines)
   * Only the budget relaxes when too few rifles fit, so an out-of-range
   * rifle is never used as filler. No brand fills more than three slots.
   */
  function renderRecs(scope) {
    var box = scope.querySelector('[data-bb-recs]');
    if (!box) return;
    var grid = box.querySelector('[data-bb-recs-grid]');
    var pool = Array.prototype.slice.call(box.querySelectorAll('template[data-bb-rec]'));
    if (!grid || !pool.length) return;

    function pick(re) {
      var el = scope.querySelector('input[data-bb-choice]:checked[name*="' + re + '"]');
      return el ? el.value.toLowerCase() : '';
    }
    var mission = pick('Mission');
    var distance = pick('Distance');
    var budget = pick('Investment');

    /* Practical effective range per chambering, in yards: roughly where the
       best match/solid loads for it stop being supersonic and predictable at
       standard conditions. First match wins, so specific names come first.
       50 BMG is capped at 2,000: it is not an ELR-optimized chambering. */
    var REACH = [
      [/22 ?lr/, 300],
      [/300 ?(blackout|blk|aac)/, 500],
      [/223|5\.56/, 700],
      [/6\.5x284/, 1300],
      [/6\.5x47/, 1200],
      [/6\.5 ?prc/, 1400],
      [/6\.5 ?creed/, 1200],
      [/6 ?creed/, 1100],
      [/6 ?xc|dasher|6 ?gt/, 1100],
      [/6 ?(mm ?)?br/, 1000],
      [/260/, 1100],
      [/243/, 1000],
      [/284/, 1200],
      [/30-06|308/, 1000],
      [/7 ?mm ?(rem|mag)/, 1500],
      [/7 ?(mm ?)?prc/, 1600],
      [/300 ?norma/, 1950],
      [/300 ?(rum|ultra)|300 ?prc/, 1700],
      [/300 ?win/, 1500],
      [/300 ?wsm/, 1400],
      [/338 ?norma/, 1800],
      [/338/, 1750],
      [/50 ?bmg|\.50( |$)/, 2000],
      [/416/, 2500],
      [/375 ?enabelr/, 2500],
      [/408/, 3000],
      [/375 ?(cheytac|ct)/, 3500]
    ];
    function reachOf(cal) {
      for (var r = 0; r < REACH.length; r++) if (REACH[r][0].test(cal)) return REACH[r][1];
      return 0;
    }
    /* a chambering has to reach the far end of the chosen band; at shorter
       bands it also can't be gross overkill (no CheyTac for 800 yd) */
    var need = 0, most = Infinity, open = false;
    if (/under|< ?1,?000/.test(distance)) { need = 1000; most = 1600; }
    else if (/1,?000\D+1,?500/.test(distance)) { need = 1500; most = 1950; }
    else if (/1,?500\D+2,?000/.test(distance)) need = 2000;
    else if (/2,?000\D+3,?000/.test(distance)) need = 3000;
    else if (/3,?000/.test(distance)) { need = 3000; open = true; }
    var m = /extreme|elr/.test(mission) ? 'elr' : /hunt/.test(mission) ? 'hunt' : /compet/.test(mission) ? 'comp' : /precision|target/.test(mission) ? 'prec' : '';

    var nums = (budget.match(/\d[\d,]*/g) || []).map(function (n) { return Number(n.replace(/,/g, '')); });
    var hi = Infinity, lo = 0;
    if (/under/.test(budget) && nums.length) hi = nums[0];
    else if (/\+/.test(budget) && nums.length) lo = nums[0];
    else if (nums.length >= 2) { lo = nums[0]; hi = nums[1]; }
    var cap = hi === Infinity ? Infinity : hi * 0.7;
    var target = hi !== Infinity ? (lo + hi) / 2 * 0.55 : lo ? Math.max(12000, lo * 0.25) : 9000;

    var items = pool.map(function (t) {
      var d = t.dataset;
      var cals = (d.cals || '').split('|').filter(Boolean);
      var reaches = cals.map(reachOf);
      /* a multi-caliber rifle qualifies on its best-fitting chambering */
      var fit = reaches.filter(function (y) { return y >= need && y <= most; });
      return { t: t, price: Number(d.price) || 0, type: (d.type || '').toLowerCase(), brand: (d.brand || '').toLowerCase(), name: d.name || '',
        reach: Math.max.apply(null, reaches.concat(0)), fit: fit.length ? Math.min.apply(null, fit) : 0 };
    }).filter(function (x) {
      /* hard rule in every pass: no caliber data or no chambering in range, no card */
      return x.price >= 1000 && (!need || x.fit > 0);
    });

    /* right-sized beats overkill; for 3,000+ more reach is simply better */
    function fitScore(x) {
      if (!need) return 0;
      if (open) return 2.5 * Math.min(1, x.fit / 3500);
      return 2.5 * (1 - Math.min(1, (x.fit - need) / need));
    }
    function score(x) {
      var s = 0;
      if (/victrix/.test(x.brand)) s += 3;
      else if (/cadex/.test(x.brand)) s += 1.5;
      if (m === 'elr' && /elr|extreme/.test(x.type)) s += 2;
      if (m === 'prec' && /precision/.test(x.type)) s += 1;
      if (m === 'comp' && /venus|target|blackbelt|performance|motion|kinetic|scepter|orb|prs|f-class|competition/.test(x.name)) s += 2;
      if (m === 'hunt' && /hunt|lynx|lupus|ardea|walnut|mountain|light/.test(x.name)) s += 2;
      if (m === 'hunt' && x.reach > 2000) s -= 3;
      if (target) s += 2 * (1 - Math.min(1, Math.abs(x.price - target) / target));
      return s + fitScore(x);
    }
    var chosen = [];
    /* only the budget relaxes; range never does */
    var passes = [
      function (x) { return x.price <= cap; },
      function () { return true; }
    ];
    for (var p = 0; p < passes.length && chosen.length < 4; p++) {
      var ranked = items.filter(passes[p]).filter(function (x) { return chosen.indexOf(x) < 0; })
        .sort(function (a, b) { return score(b) - score(a) || b.price - a.price; });
      for (var i = 0; i < ranked.length && chosen.length < 4; i++) {
        var same = chosen.filter(function (c) { return c.brand === ranked[i].brand; }).length;
        if (same < 3) chosen.push(ranked[i]);
      }
    }
    if (!chosen.length) return;
    grid.innerHTML = '';
    chosen.forEach(function (x) { grid.appendChild(document.importNode(x.t.content, true)); });
    box.hidden = false;
    if (typeof initCardMedia === 'function') initCardMedia(grid);
    bbTrack('bb_system_recs_shown', { count: chosen.length, mission: mission, distance: distance, investment: budget });
  }

  function initSystemForms(root) {
    var wraps = root.querySelectorAll('[data-bb-leadform]:not([data-bb-bound])');

    for (var i = 0; i < wraps.length; i++) {
      (function (wrap) {
        wrap.setAttribute('data-bb-bound', '');

        var form = wrap.querySelector('form');
        var iframe = wrap.querySelector('[data-bb-leadframe]');
        var submit = wrap.querySelector('[data-bb-leadsubmit]');
        var panel = wrap.querySelector('[data-bb-leadpanel]');
        var success = wrap.querySelector('[data-bb-leadsuccess]');
        var alertBox = wrap.querySelector('[data-bb-leadalert]');
        if (!form || !iframe || !submit) return;

        var label = submit.textContent;
        var done = false;

        function showSuccess() {
          if (done) return;
          done = true;
          if (panel) panel.hidden = true;
          if (success) {
            success.hidden = false;
            try { renderRecs(wrap); } catch (e) { /* recommendations are a nicety */ }
            if (typeof success.scrollIntoView === 'function') {
              success.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }
          bbTrack('bb_system_lead_submitted', {
            path: window.location.pathname,
            objective: (form.querySelector('[name="contact[Objective]"]:checked') || {}).value || ''
          });
        }

        form.addEventListener(
          'submit',
          function (e) {
            e.preventDefault();
          },
          true
        );

        submit.addEventListener('click', function (event) {
          event.preventDefault();

          if (typeof form.reportValidity === 'function' && !form.reportValidity()) {
            bbTrack('bb_system_lead_invalid', { path: window.location.pathname });
            return;
          }

          if (alertBox) alertBox.hidden = true;
          submit.disabled = true;
          submit.textContent = submit.getAttribute('data-bb-sending') || 'Sending...';

          iframe.onload = function () {
            window.setTimeout(showSuccess, 120);
          };

          try {
            form.setAttribute('target', iframe.getAttribute('name'));
            form.submit();
            // Iframe onload is unreliable for this endpoint; timed fallback.
            window.setTimeout(showSuccess, 1400);
          } catch (e) {
            if (alertBox) alertBox.hidden = false;
            submit.disabled = false;
            submit.textContent = label;
          }
        });
      })(wraps[i]);
    }
  }

  /* -------------------------------------------------- system configurator --- */
  /**
   * Turns the Build My ELR System markup into a guided, one-decision-per-screen
   * configurator.
   *
   * Progressive enhancement: with JavaScript unavailable every step stays
   * visible and the form submits as an ordinary contact form, so no answer is
   * ever lost to a script failure.
   */
  function initConfigurator(root) {
    var wrap = root.querySelector('[data-bb-configurator]:not([data-bb-cfg-bound])');
    if (!wrap) return;
    wrap.setAttribute('data-bb-cfg-bound', '');

    var steps = Array.prototype.slice.call(wrap.querySelectorAll('[data-bb-step]'));
    if (!steps.length) return;

    var order = steps.map(function (s) { return s.getAttribute('data-bb-step'); });
    var index = 0;

    // Only the intro is visible until the visitor starts.
    function show(i, focusIt) {
      index = Math.max(0, Math.min(i, steps.length - 1));
      steps.forEach(function (s, n) { s.hidden = n !== index; });

      if (order[index] === 'review') buildProfile();

      if (focusIt !== false) {
        var h = steps[index].querySelector('h1, h2');
        if (h) {
          h.setAttribute('tabindex', '-1');
          try { h.focus({ preventScroll: true }); } catch (e) { /* nicety */ }
        }
        var top = wrap.getBoundingClientRect().top + window.pageYOffset - 90;
        window.scrollTo({ top: top, behavior: 'smooth' });
      }
    }

    // Everything hidden except the intro, once JS is confirmed running.
    steps.forEach(function (s, n) { s.hidden = n !== 0; });

    /** Human-readable summary of every answer so far. */
    function answers() {
      var out = [];
      steps.forEach(function (s) {
        var key = s.getAttribute('data-bb-step');
        if (key === 'intro' || key === 'review') return;

        var label = s.querySelector('.bb-prog__label');
        var picked = s.querySelector('[data-bb-choice]:checked');
        if (picked) {
          out.push({ k: label ? label.textContent.trim() : key, v: picked.value });
          return;
        }
        if (key === 'contact') {
          var f = s.querySelector('[name="contact[First Name]"]');
          var l = s.querySelector('[name="contact[Last Name]"]');
          var name = ((f && f.value) || '') + ' ' + ((l && l.value) || '');
          if (name.trim()) out.push({ k: 'Contact', v: name.trim() });
        }
      });
      return out;
    }

    function buildProfile() {
      var list = wrap.querySelector('[data-bb-profile-list]');
      if (!list) return;
      list.innerHTML = '';
      answers().forEach(function (row) {
        var dt = document.createElement('dt');
        dt.className = 'bb-spec__k';
        dt.textContent = row.k;
        var dd = document.createElement('dd');
        dd.className = 'bb-spec__v';
        dd.textContent = row.v;
        list.appendChild(dt);
        list.appendChild(dd);
      });
    }

    /** A question step needs a selection; the contact step needs valid fields. */
    function validate(step) {
      var key = step.getAttribute('data-bb-step');

      if (key === 'contact') {
        var fields = Array.prototype.slice.call(step.querySelectorAll('input[required]'));
        for (var i = 0; i < fields.length; i++) {
          if (!fields[i].checkValidity()) {
            fields[i].reportValidity();
            return false;
          }
        }
        return true;
      }

      var group = step.querySelector('[data-bb-choice]');
      if (!group) return true;
      if (step.querySelector('[data-bb-choice]:checked')) return true;

      step.classList.add('is-missing');
      var msg = step.querySelector('[data-bb-missing]');
      if (msg) msg.hidden = false;
      var first = step.querySelector('[data-bb-choice]');
      if (first) first.focus();
      return false;
    }

    wrap.addEventListener('click', function (event) {
      var t = event.target;
      if (!t || typeof t.closest !== 'function') return;

      if (t.closest('[data-bb-cfg-start]')) {
        event.preventDefault();
        bbTrack('bb_cfg_started', { path: window.location.pathname });
        show(1);
        return;
      }
      if (t.closest('[data-bb-cfg-next]')) {
        event.preventDefault();
        if (!validate(steps[index])) return;
        bbTrack('bb_cfg_step', { step: order[index] });
        show(index + 1);
        return;
      }
      if (t.closest('[data-bb-cfg-back]')) {
        event.preventDefault();
        show(index - 1);
      }
    });

    // Selection: reveal contextual expertise and any dependent field.
    wrap.addEventListener('change', function (event) {
      var input = event.target;
      if (!input || !input.hasAttribute || !input.hasAttribute('data-bb-choice')) return;

      var step = input.closest('[data-bb-step]');
      if (!step) return;

      var ctxBox = step.querySelector('[data-bb-context]');
      var ctx = input.getAttribute('data-bb-ctx');
      if (ctxBox) {
        if (ctx) {
          ctxBox.textContent = ctx;
          ctxBox.hidden = false;
        } else {
          ctxBox.hidden = true;
          ctxBox.textContent = '';
        }
      }

      var kit = step.querySelector('[data-bb-kit]');
      if (kit) kit.hidden = input.getAttribute('data-bb-reveal-kit') !== 'true';

      /* Answering the step clears the blocked-continue warning. */
      step.classList.remove('is-missing');
      var warn = step.querySelector('[data-bb-missing]');
      if (warn) warn.hidden = true;
    });
  }

  /* ---------------------------------------------------------------- init --- */
  /* -------------------------------------------------------- disclosures --- */
  /*
   * Compact FAQ / events disclosures: native <details data-bb-disclosure>.
   * Without this script they still open and close, answers stay in the HTML
   * (searchable, find-in-page opens them), and keyboard support is native.
   * This adds two things: aria-expanded on the summary for assistive tech
   * that does not infer it, and a short height transition so opening does
   * not snap the page. Reduced-motion users get the instant native toggle.
   */
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  function initDisclosures(scope) {
    scope.querySelectorAll('details[data-bb-disclosure]').forEach(function (d) {
      if (d.hasAttribute('data-bb-bound')) return;
      d.setAttribute('data-bb-bound', '');
      var summary = d.querySelector('summary');
      var body = d.querySelector('[data-bb-disclosure-body]');
      if (!summary) return;

      /* "12 questions" in a collapsed summary, counted from what actually rendered. */
      var counter = summary.querySelector('[data-bb-count-items]');
      if (counter && body) {
        var n = body.querySelectorAll(counter.getAttribute('data-bb-count-items')).length;
        if (n) counter.textContent = n + ' ' + counter.getAttribute(n === 1 ? 'data-bb-count-one' : 'data-bb-count-many');
      }

      var sync = function () { summary.setAttribute('aria-expanded', d.open ? 'true' : 'false'); };
      sync();
      d.addEventListener('toggle', sync);

      if (!body || typeof body.animate !== 'function') return;
      var running = null;

      summary.addEventListener('click', function (event) {
        if (reduceMotion && reduceMotion.matches) return;
        event.preventDefault();
        if (running) running.cancel();
        var opening = !d.open;
        if (opening) d.open = true;
        var full = body.scrollHeight;
        body.style.overflow = 'hidden';
        running = body.animate(
          opening
            ? [{ height: '0px', opacity: 0 }, { height: full + 'px', opacity: 1 }]
            : [{ height: full + 'px', opacity: 1 }, { height: '0px', opacity: 0 }],
          { duration: 220, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)' }
        );
        running.onfinish = function () {
          if (!opening) d.open = false;
          body.style.overflow = '';
          running = null;
        };
        running.oncancel = function () { body.style.overflow = ''; };
      });
    });
  }

  /* ---------------------------------------------------------- card media --- */
  /*
   * Product photography is mixed: most shots are on white or cut out, some on
   * solid black, a few are lifestyle scenes. Reading the edge of each loaded
   * photo lets its tile match: black edges -> dark tile, busy edges -> photo
   * fills the tile, white/transparent -> the default light tile. Product
   * images are served same-origin (/cdn/shop), so the canvas is readable; if
   * it ever is not, the default tile simply stays.
   */
  function classifyCardImage(img) {
    var media = img.closest('[data-bb-card-media]');
    if (!media || media.hasAttribute('data-bb-bg')) return;
    try {
      var S = 40;
      var c = document.createElement('canvas');
      c.width = S; c.height = S;
      var g = c.getContext('2d', { willReadFrequently: true });
      g.drawImage(img, 0, 0, S, S);
      var px = g.getImageData(0, 0, S, S).data;
      var n = 0, clear = 0, dark = 0, light = 0;
      for (var y = 0; y < S; y++) {
        for (var x = 0; x < S; x++) {
          if (x > 1 && x < S - 2 && y > 1 && y < S - 2) continue; /* edge band only */
          var k = (y * S + x) * 4;
          n++;
          if (px[k + 3] < 20) { clear++; continue; }
          var l = 0.2126 * px[k] + 0.7152 * px[k + 1] + 0.0722 * px[k + 2];
          if (l < 35) dark++; else if (l > 225) light++;
        }
      }
      /* Any real transparency means a cut-out (collages touch the edges), so
         only an opaque, busy edge counts as a lifestyle scene. */
      var bg = clear / n > 0.35 || (clear + light) / n > 0.6 ? 'light' : dark / n > 0.85 ? 'dark' : 'scene';
      media.setAttribute('data-bb-bg', bg);
      /* A scene only fills the frame when its shape is close to the tile's,
         so nothing meaningful is cropped; a long barrel shot stays whole. */
      if (bg === 'scene') {
        var tile = media.clientWidth / Math.max(1, media.clientHeight);
        var shot = img.naturalWidth / Math.max(1, img.naturalHeight);
        media.setAttribute('data-bb-fit', Math.abs(shot / tile - 1) < 0.2 ? 'cover' : 'contain');
      }
    } catch (e) {
      media.setAttribute('data-bb-bg', 'light');
    }
  }

  function initCardMedia(scope) {
    scope.querySelectorAll('[data-bb-card-media] img').forEach(function (img) {
      if (img.complete && img.naturalWidth) classifyCardImage(img);
    });
  }

  /* One capture listener covers every card, including those the facet
     engine inserts after filtering (load does not bubble, but it captures). */
  document.addEventListener('load', function (event) {
    var t = event.target;
    if (t && t.tagName === 'IMG' && t.closest && t.closest('[data-bb-card-media]')) classifyCardImage(t);
  }, true);

  function init(root) {
    var scope = root || document;
    initCardMedia(scope);
    initDisclosures(scope);
    initReveal(scope);
    initConfigurator(scope);
    initSystemForms(scope);
    initStickyBuy();
    initDrawer();
  }

  function boot() {
    init(document);
    initTracking();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
